import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ReactiveFormsModule } from '@angular/forms'

import { Store } from '@ngrx/store'
import { ColDef, GridApi, ICellRendererParams } from 'ag-grid-community'
import { MenuItem } from 'primeng/api'
import { Menu, MenuModule } from 'primeng/menu'
import { TooltipModule } from 'primeng/tooltip'
import { v4 as uuid } from 'uuid'

import { PlatformComponents, PlatformModules, PlatformScopes } from '@flowx/designer/app.constants'
import {
  AiDocumentContentType,
  AiDocumentStatus,
  AiDocumentSummary,
  AiSelectors,
} from '@flowx/designer/modules/new-ai'
import {
  DefaultActionsColumn,
  DefaultAuditColumns,
  FlxResourcesGridComponent,
} from '@flowx/designer/modules/projects/components/resources-grid/resources-grid.component'
import { FlxResourcesPageComponent } from '@flowx/designer/modules/projects/components/resources-page/flx-resources-page.component'
import { DeleteDefaultMenuItem } from '@flowx/designer/modules/projects/components/resources-page/menu-items.constants'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { getAccessRights } from '@flowx/designer/shared/services/permissions.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { FlxFormControlModule } from '@flowx/angular-ui-toolkit'

import { AiDocumentIntelligenceService } from './ai-document-intelligence.service'
import { fetchStatusPropsByType } from './ai-document-intelligence.utils'

import { resetInputFileValueFromEvent } from '../../../../common/ai.utils'

const FilenameColumn: ColDef = {
  headerName: 'Name',
  field: 'filename' satisfies keyof AiDocumentSummary,
  tooltipField: 'filename',
  flex: 1,
  minWidth: 85,
}
const ContentsColumn: ColDef = {
  headerName: 'Contents',
  field: 'contents' satisfies keyof AiDocumentSummary,
  flex: 0,
  width: 400,
  cellRenderer: (params: ICellRendererParams): string => {
    const availableContents = params?.value
      ? Object.keys(params.value).filter((key) => params.value[key])
      : []

    const getLabelForContentType = (contentType): string =>
      contentsLabelMap[contentType as AiDocumentContentType] ?? ''

    return `<div class="flex gap-1 h-full items-center">
    ${availableContents
      .map(
        (contentType) =>
          `<div class="px-2 py-1 bg-neutral-100 text-sm rounded-lg">
            ${getLabelForContentType(contentType)}
          </div>`
      )
      .join('')}
  </div>`
  },
}
const StatusColumn: ColDef = {
  headerName: 'Status',
  field: 'status' satisfies keyof AiDocumentSummary,
  tooltipField: 'statusTooltip',
  flex: 0,
  width: 150,
  cellRenderer: (params: ICellRendererParams): string => {
    const status = params?.value as AiDocumentStatus
    const statusProps = fetchStatusPropsByType(status)

    return `<div class="flex items-center gap-1 h-full">
    <div class="px-3 py-0.5 font-semibold text-white text-sm rounded-full capitalize ${statusProps?.bgColor ?? 'bg-neutral-100'}">
      ${statusProps?.label}
    </div>
  </div>`
  },
}

const contentsLabelMap: Record<AiDocumentContentType, string> = {
  [AiDocumentContentType.BUSINESS_PROCESS]: 'BPMN',
  [AiDocumentContentType.SCREEN_NAME]: 'UI',
  [AiDocumentContentType.BUSINESS_RULE]: 'Business Rules',
  [AiDocumentContentType.SCREEN_EXPRESSION]: 'Screen Expression',
}

@Component({
  selector: 'flx-ai-document-intelligence',
  templateUrl: './ai-document-intelligence.component.html',
  styleUrl: './ai-document-intelligence.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AiDocumentIntelligenceService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TooltipModule,
    FlxFormControlModule,
    FlxResourcesGridComponent,
    FlxResourcesPageComponent,
    MenuModule,
  ],
})
export class AiDocumentIntelligenceComponent {
  #destroyRef = inject(DestroyRef)
  #store = inject(Store)
  #toastMsgService = inject(ToastMessageService)
  #documentsService = inject(AiDocumentIntelligenceService)
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #mediaLibraryAccessRights =
    getAccessRights()[PlatformComponents.CMS][PlatformModules.MEDIA_LIBRARY]

  actionsMenu = viewChild<Menu>('actionsMenu')

  readOnly = this.#applicationDetailsStore.isVersionCommitted
  canEdit = this.#mediaLibraryAccessRights[PlatformScopes.EDIT]
  canDelete = this.#mediaLibraryAccessRights[PlatformScopes.ADMIN]
  contextMenuItems: Signal<MenuItem[]> = computed(() => {
    const readOnly = this.readOnly()
    return [
      {
        ...DeleteDefaultMenuItem(readOnly, this.canDelete),
        command: (): void => {
          this.onDeleteDocument(this.selectedAiDocumentSummary)
        },
      },
    ]
  })

  documentsLoading = signal<boolean>(false)
  allowedDocumentFileTypes = this.#store.selectSignal(AiSelectors.allowedDocumentFileTypes)

  documentsTableDef: ColDef[] = [
    FilenameColumn,
    ContentsColumn,
    StatusColumn,
    ...DefaultAuditColumns,
    {
      ...DefaultActionsColumn,
      cellRendererParams: {
        actions: [
          {
            icon: 'ph-dots-three-vertical',
            tooltip: '',
            dataTestId: 'row-menu-button',
            clickAction: (data, event) => this.openMenuByContext(event, data),
          },
        ],
      },
    },
  ]

  gridOptions = computed(() => ({
    rowModelType: 'infinite',
    datasource: this.#documentsService.gridDataSource(),
  }))

  selectedAiDocumentSummary: AiDocumentSummary

  onGridReady(gridApi: GridApi): void {
    this.#documentsService.setGridApi(gridApi)
  }

  onSearchQueryChange(value: string): void {
    this.#documentsService.search({ filename: value })
    this.#documentsService.refresh()
  }

  openMenuByContext(event: Event, document: AiDocumentSummary): void {
    const menuItems = this.contextMenuItems()

    if (document) {
      this.selectedAiDocumentSummary = document
    }
    this.actionsMenu().model = menuItems
    this.actionsMenu().toggle(event)
  }

  onDeleteDocument(document: AiDocumentSummary): void {
    this.#documentsService
      .deleteDocument(document)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.#documentsService.refresh()
          this.#toastMsgService.showSuccessMessage('Document deleted successfully')
        },
        error: (error) => {
          this.#toastMsgService.showErrorMessage(error.title || 'Error deleting document')
        },
      })
  }

  async onDocumentSelected(event: Event): Promise<void> {
    const files = Array.from((event.target as HTMLInputElement).files).map((file) => ({
      file,
      name: file.name,
      type: file.type,
      id: uuid(),
    }))

    if (files.length > 1) {
      resetInputFileValueFromEvent(event)
      throw new Error('Multiple files are not supported')
    }

    this.#documentsService
      .uploadDocument(files[0])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#documentsService.refresh()
        resetInputFileValueFromEvent(event)
      })
  }
}
